import { useState, useCallback, useRef } from "react"
import {
  ConnectorFieldMapping,
  ConnectorFieldMappingEntry,
  ConnectorDataType,
  ConnectorDirection
} from "../../../../../../types"
import {
  LEAD_FIELD_DEFINITIONS,
  SUBSCRIPTION_FIELD_DEFINITIONS,
  PENDING_SUBSCRIPTION_FIELD_DEFINITIONS,
  LEAD_SCORE_FIELD_DEFINITIONS,
  FieldDefinition
} from "../../../../../constants/connector-field-types"

interface UseFieldMappingProps {
  fieldMapping: ConnectorFieldMapping
  dataType: ConnectorDataType
  direction: ConnectorDirection
  onChange: (mapping: ConnectorFieldMapping) => void
}

export const useFieldMapping = ({
  fieldMapping,
  dataType,
  direction,
  onChange
}: UseFieldMappingProps) => {
  const isIncoming = direction === "incoming"
  const [newBbFieldToAdd, setNewBbFieldToAdd] = useState<string>("")
  const [newCrmFieldToAdd, setNewCrmFieldToAdd] = useState<string>("")

  // Create stable IDs for each field mapping entry using ref
  const fieldIdsRef = useRef<Map<number, string>>(new Map())
  const nextIdRef = useRef(0)

  const getFieldDefinitions = (): FieldDefinition[] => {
    switch (dataType) {
      case "subscription":
        return SUBSCRIPTION_FIELD_DEFINITIONS
      case "pending_subscription":
        return PENDING_SUBSCRIPTION_FIELD_DEFINITIONS
      case "lead_score":
        return LEAD_SCORE_FIELD_DEFINITIONS
      default:
        return LEAD_FIELD_DEFINITIONS
    }
  }

  const getFieldNames = useCallback(
    (
      entry: ConnectorFieldMappingEntry
    ): { bbField: string; crmField: string } => {
      const bbField = entry.key || ""
      const crmField = isIncoming
        ? entry.incoming_key || ""
        : entry.outgoing_key || ""
      return { bbField, crmField }
    },
    [isIncoming]
  )

  const getStableId = useCallback((index: number): string => {
    const fieldIds = fieldIdsRef.current
    if (!fieldIds.has(index)) {
      fieldIds.set(index, `field-${nextIdRef.current++}-${Math.random()}`)
    }
    return fieldIds.get(index)!
  }, [])

  // Rebuild stable IDs when array length changes to keep them in sync
  const ensureStableIds = useCallback((mapping: ConnectorFieldMapping) => {
    const fieldIds = fieldIdsRef.current
    // Clean up IDs for indices beyond the array length
    fieldIds.forEach((_, key) => {
      if (key >= mapping.length) {
        fieldIds.delete(key)
      }
    })
    // Ensure IDs exist for all entries
    mapping.forEach((_, index) => {
      if (!fieldIds.has(index)) {
        fieldIds.set(index, `field-${nextIdRef.current++}-${Math.random()}`)
      }
    })
  }, [])

  // Ensure IDs are in sync on each render
  ensureStableIds(fieldMapping)

  const getMappedBBFields = useCallback((): Set<string> => {
    return new Set(fieldMapping.map(entry => entry.key).filter(Boolean))
  }, [fieldMapping])

  const getAvailableFields = useCallback((): FieldDefinition[] => {
    const mappedBBFields = getMappedBBFields()
    return getFieldDefinitions().filter(
      field => !mappedBBFields.has(field.value)
    )
  }, [getMappedBBFields])

  const addFieldMapping = useCallback(() => {
    // Skip if "__custom__" placeholder is still selected
    if (newBbFieldToAdd === "__custom__") {
      return
    }

    if (isIncoming) {
      if (newCrmFieldToAdd && newBbFieldToAdd) {
        const newEntry: ConnectorFieldMappingEntry = {
          incoming_key: newCrmFieldToAdd,
          key: newBbFieldToAdd
        }
        onChange([...fieldMapping, newEntry])
        setNewCrmFieldToAdd("")
        setNewBbFieldToAdd("")
      }
    } else {
      if (newBbFieldToAdd) {
        const newEntry: ConnectorFieldMappingEntry = {
          key: newBbFieldToAdd,
          outgoing_key: ""
        }
        onChange([...fieldMapping, newEntry])
        setNewBbFieldToAdd("")
      }
    }
  }, [isIncoming, newCrmFieldToAdd, newBbFieldToAdd, fieldMapping, onChange])

  const addCustomFieldMapping = useCallback(
    (customBbField: string) => {
      if (isIncoming) {
        if (newCrmFieldToAdd && customBbField) {
          const newEntry: ConnectorFieldMappingEntry = {
            incoming_key: newCrmFieldToAdd,
            key: customBbField
          }
          onChange([...fieldMapping, newEntry])
          setNewCrmFieldToAdd("")
          setNewBbFieldToAdd("")
        }
      } else {
        if (customBbField) {
          const newEntry: ConnectorFieldMappingEntry = {
            key: customBbField,
            outgoing_key: ""
          }
          onChange([...fieldMapping, newEntry])
          setNewBbFieldToAdd("")
        }
      }
    },
    [isIncoming, newCrmFieldToAdd, fieldMapping, onChange]
  )

  const removeFieldMapping = useCallback(
    (index: number) => {
      const newMapping = fieldMapping.filter((_, i) => i !== index)
      // Rebuild stable IDs: shift IDs down for entries after the removed one
      const fieldIds = fieldIdsRef.current
      const newIds = new Map<number, string>()
      let newIdx = 0
      for (let i = 0; i < fieldMapping.length; i++) {
        if (i !== index) {
          const existingId = fieldIds.get(i)
          if (existingId) {
            newIds.set(newIdx, existingId)
          }
          newIdx++
        }
      }
      fieldIdsRef.current = newIds
      onChange(newMapping)
    },
    [fieldMapping, onChange]
  )

  const updateMapping = useCallback(
    (index: number, newBbField: string, newCrmField: string) => {
      const newMapping = [...fieldMapping]
      const entry = { ...newMapping[index] }

      entry.key = newBbField
      if (isIncoming) {
        entry.incoming_key = newCrmField
      } else {
        entry.outgoing_key = newCrmField
      }

      newMapping[index] = entry
      onChange(newMapping)
    },
    [fieldMapping, isIncoming, onChange]
  )

  return {
    isIncoming,
    newBbFieldToAdd,
    setNewBbFieldToAdd,
    newCrmFieldToAdd,
    setNewCrmFieldToAdd,
    getFieldDefinitions,
    getFieldNames,
    getStableId,
    getMappedBBFields,
    getAvailableFields,
    addFieldMapping,
    addCustomFieldMapping,
    removeFieldMapping,
    updateMapping
  }
}
